import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Lock, Save, Plus, Edit, Trash2, Printer, ExternalLink, Download, Upload, Tag, LifeBuoy } from 'lucide-react';
import { SEO } from '../../components/common/SEO';
import { Header } from '../../components/layout/Header';
import { ProductFields } from '../../components/smartqr/ProductFields';
import { LocationFields } from '../../components/smartqr/LocationFields';
import { API, SUPPORT_WA, PREMIUM_AMOUNT, waLink, imgSrc, uploadImage, downloadFile, inputCls, labelCls, SMART_QR_CATEGORIES } from '../../components/smartqr/smartqrApi';

const emptyProduct = { name: '', price: '', image: '', description: '' };
const bizFields = [['business_name', 'Business Name', 'text'], ['phone', 'Phone', 'tel'], ['whatsapp', 'WhatsApp', 'tel']];
const LOC_KEYS = ['country', 'state', 'district', 'city', 'area', 'pincode', 'address'];
const linkFields = [['google_review_link', 'Google Review Link'], ['website', 'Website'], ['instagram', 'Instagram'], ['facebook', 'Facebook'], ['youtube', 'YouTube']];

const LockedScreen = ({ code }) => (
  <div className="max-w-md mx-auto px-4 py-12 text-center" data-testid="management-locked">
    <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4"><Lock className="w-8 h-8 text-orange-500" /></div>
    {code === 'PREMIUM_REQUIRED' ? (
      <>
        <h1 className="text-xl font-extrabold text-slate-800 mb-2">SMART QR PREMIUM REQUIRED</h1>
        <p className="text-sm text-slate-600 mb-5">Upgrade for ₹{PREMIUM_AMOUNT} one time to manage your business page, update products and download your branded QR poster.</p>
        <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-sm font-bold text-orange-700" data-testid="locked-unlock-btn"><Lock className="w-4 h-4 inline mr-1" /> UNLOCK PREMIUM — ₹{PREMIUM_AMOUNT}</div>
        <p className="text-xs text-slate-500 mt-3">Open your private <span className="font-semibold">My QR</span> link and tap Unlock Premium — a secure Razorpay payment link is generated for your business and Premium activates automatically after payment.</p>
      </>
    ) : (
      <>
        <h1 className="text-xl font-extrabold text-slate-800 mb-2">Invalid management link</h1>
        <p className="text-sm text-slate-600 mb-5">This link is not valid. Please use the management link from your My QR page.</p>
      </>
    )}
    <a href={waLink(SUPPORT_WA, 'Hi JustOffs, I have a Smart Business QR Premium account and need help recovering my management link.\nBusiness Name: \nWhatsApp Number: ')} target="_blank" rel="noreferrer" className="mt-4 text-xs text-slate-500 hover:text-orange-600 inline-flex items-center gap-1" data-testid="locked-contact"><LifeBuoy className="w-3.5 h-3.5" /> Lost your management link? Contact JustOffs</a>
  </div>
);

export const ManagePage = ({ adminMode = false }) => {
  const { businessId, token } = useParams();
  const base = adminMode ? `${API}/api/smart-qr/admin/businesses/${businessId}` : `${API}/api/smart-qr/manage/${businessId}/${token}`;
  const authCfg = adminMode ? { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } } : {};
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(emptyProduct);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(base, authCfg);
      setData(res.data);
      setForm({ ...res.data.business });
    } catch (e) {
      setLocked(e.response?.data?.detail?.code || (e.response?.status === 404 ? 'NOT_FOUND' : 'INVALID_TOKEN'));
    }
  }, [base]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const err = (e, fallback) => { const d = e.response?.data?.detail; toast.error(typeof d === 'string' ? d : Array.isArray(d) && d[0]?.msg ? d[0].msg.replace('Value error, ', '') : fallback); };

  const saveBusiness = async () => {
    setSaving(true);
    try {
      const payload = {};
      ['business_name', 'category', 'phone', 'whatsapp', 'logo', ...LOC_KEYS, ...linkFields.map(([k]) => k)].forEach((k) => { if (form[k] !== undefined && form[k] !== null) payload[k] = form[k]; });
      if (adminMode) ['description', 'featured', 'is_demo', 'disabled'].forEach((k) => { if (form[k] !== undefined && form[k] !== null) payload[k] = form[k]; });
      await axios.put(base, payload, authCfg);
      toast.success('Business page updated. Your QR stays the same.');
      load();
    } catch (e) { err(e, 'Could not save changes'); }
    setSaving(false);
  };

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { set('logo', await uploadImage(file)); toast.success('Logo uploaded. Click Save to apply.'); } catch { toast.error('Logo upload failed'); }
  };

  const saveProduct = async () => {
    if (!draft.name?.trim()) return toast.error('Product name is required');
    const body = { ...draft, price: draft.price || null, image: draft.image || null, description: draft.description || null };
    try {
      if (editing) await axios.put(`${base}/products/${editing}`, body, authCfg);
      else await axios.post(`${base}/products`, body, authCfg);
      toast.success(editing ? 'Product updated' : 'Product added');
      setEditing(null); setAdding(false); setDraft(emptyProduct);
      load();
    } catch (e) { err(e, 'Could not save product'); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await axios.delete(`${base}/products/${id}`, authCfg); toast.success('Product deleted'); load(); } catch (e) { err(e, 'Could not delete'); }
  };

  if (locked) return <div className="min-h-screen bg-slate-50"><Header /><LockedScreen code={locked} /></div>;
  if (!data) return <div className="min-h-screen bg-slate-50"><Header /><div className="p-10 text-center text-slate-500">Loading...</div></div>;

  const products = data.products;
  const slug = data.business.public_slug;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="manage-page">
      <SEO title={`Manage ${data.business.business_name} - Smart Business QR`} description="Manage your JustOffs Smart Business QR page" />
      <Header />
      <div className="max-w-xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <div><h1 className="text-xl font-extrabold text-slate-800">{adminMode ? 'Admin: Edit Smart QR Business' : 'Manage Business Page'}</h1>{adminMode ? <p className="text-xs text-slate-500">{data.is_demo ? 'DEMO / SAMPLE business' : 'Genuine business'} · {data.premium_active ? 'Premium' : 'Free'}</p> : <p className="text-xs text-green-600 font-semibold">Premium Active ✓</p>}</div>
          <Link to={`/b/${slug}`} className="text-xs font-semibold text-orange-600 flex items-center gap-1" data-testid="manage-view-page"><ExternalLink className="w-3.5 h-3.5" /> View page</Link>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {data.poster_url && <button onClick={() => { toast.info('Generating poster...'); downloadFile(data.poster_url, `justoffs-qr-poster-${slug}.png`); }} className="bg-slate-800 text-white py-3 rounded-xl font-semibold text-xs flex items-center justify-center gap-2" data-testid="manage-poster-btn"><Printer className="w-4 h-4" /> BRANDED QR POSTER</button>}
          <button onClick={() => downloadFile(data.qr_url, `justoffs-smart-qr-${slug}.png`)} className="bg-white border border-slate-200 text-slate-700 py-3 rounded-xl font-semibold text-xs flex items-center justify-center gap-2" data-testid="manage-qr-btn"><Download className="w-4 h-4" /> DOWNLOAD QR</button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="manage-business-section">
          <h2 className="text-sm font-extrabold text-slate-800">BUSINESS INFORMATION</h2>
          {bizFields.map(([k, label, type]) => (
            <div key={k}><label className={labelCls}>{label}</label><input type={type} value={form[k] || ''} onChange={(e) => set(k, e.target.value)} className={inputCls} data-testid={`manage-${k}-input`} /></div>
          ))}
          <div><label className={labelCls}>Category</label>
            <select value={form.category || ''} onChange={(e) => set('category', e.target.value)} className={inputCls} data-testid="manage-category-select">
              {SMART_QR_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Business description</label><textarea value={form.description || ''} onChange={(e) => set('description', e.target.value)} className={`${inputCls} min-h-[70px]`} maxLength={500} placeholder="Short description shown in About" data-testid="manage-description-input" /></div>
          {adminMode && (
            <div className="grid grid-cols-3 gap-2 bg-slate-50 rounded-xl p-3" data-testid="admin-flags">
              {[['featured', 'Featured'], ['is_demo', 'Demo / Sample'], ['disabled', 'Unpublished']].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-xs font-semibold text-slate-700"><input type="checkbox" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} className="accent-orange-500" data-testid={`admin-flag-${k}`} /> {label}</label>
              ))}
            </div>
          )}
          <h2 className="text-sm font-extrabold text-slate-800 pt-2">LOCATION</h2>
          <LocationFields value={Object.fromEntries(LOC_KEYS.map((k) => [k, form[k] || (k === 'country' ? 'India' : '')]))} onChange={(v) => setForm((f) => ({ ...f, ...v }))} testPrefix="manage-loc" />
          <h2 className="text-sm font-extrabold text-slate-800 pt-2">BUSINESS BRANDING (Logo / Shop Picture)</h2>
          <label className="flex items-center gap-3 border border-dashed border-slate-300 rounded-lg px-3 py-3 text-sm text-slate-500 cursor-pointer hover:border-orange-400">
            {form.logo ? <img src={imgSrc(form.logo)} alt="logo" className="w-10 h-10 rounded-lg object-cover" /> : <Upload className="w-5 h-5" />}
            <span>{form.logo ? 'Change logo' : 'Upload logo'}</span>
            <input type="file" accept="image/*" onChange={handleLogo} className="hidden" data-testid="manage-logo-input" />
          </label>
          <h2 className="text-sm font-extrabold text-slate-800 pt-2">GOOGLE REVIEW & BUSINESS LINKS</h2>
          {linkFields.map(([k, label]) => (
            <div key={k}><label className={labelCls}>{label}</label><input value={form[k] || ''} onChange={(e) => set(k, e.target.value)} className={inputCls} placeholder="https://" data-testid={`manage-${k}-input`} /></div>
          ))}
          <button onClick={saveBusiness} disabled={saving} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="manage-save-btn"><Save className="w-4 h-4" /> {saving ? 'Saving...' : 'SAVE CHANGES'}</button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="manage-products-section">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-extrabold text-slate-800">PRODUCTS / SERVICES <span className="text-slate-400 font-medium">({products.length}/{data.product_limit})</span></h2>
            {!adding && !editing && products.length < data.product_limit && (
              <button onClick={() => { setAdding(true); setDraft(emptyProduct); }} className="text-xs font-bold text-orange-600 flex items-center gap-1" data-testid="manage-add-product-btn"><Plus className="w-4 h-4" /> ADD PRODUCT</button>
            )}
          </div>
          {(adding || editing) && (
            <div className="space-y-2">
              <ProductFields index={editing ? products.findIndex((p) => p.product_id === editing) : products.length} value={draft} onChange={setDraft} testPrefix="manage-product-form" />
              <div className="flex gap-2">
                <button onClick={saveProduct} className="flex-1 bg-slate-800 text-white py-2.5 rounded-lg font-semibold text-sm" data-testid="manage-product-save-btn">{editing ? 'Update Product' : 'Add Product'}</button>
                <button onClick={() => { setAdding(false); setEditing(null); setDraft(emptyProduct); }} className="px-4 bg-slate-100 text-slate-600 rounded-lg text-sm font-medium" data-testid="manage-product-cancel-btn">Cancel</button>
              </div>
            </div>
          )}
          {products.map((p) => (
            <div key={p.product_id} className="flex items-center gap-3 border border-slate-100 rounded-xl p-3" data-testid={`manage-product-${p.product_id}`}>
              {p.image ? <img src={imgSrc(p.image)} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" /> : <div className="w-12 h-12 rounded-lg bg-slate-100 flex-shrink-0" />}
              <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-slate-800 truncate">{p.name}</p><p className="text-xs text-orange-600 font-bold">{p.price}</p></div>
              <button onClick={() => { setEditing(p.product_id); setAdding(false); setDraft({ ...emptyProduct, ...p }); }} className="p-2 text-slate-400 hover:text-slate-700" data-testid={`manage-edit-${p.product_id}`}><Edit className="w-4 h-4" /></button>
              <button onClick={() => deleteProduct(p.product_id)} className="p-2 text-slate-400 hover:text-red-500" data-testid={`manage-delete-${p.product_id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {products.length === 0 && !adding && <p className="text-xs text-slate-400 text-center py-2">No products yet. Add your first one.</p>}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="manage-offers-section">
          <h2 className="text-sm font-extrabold text-slate-800 mb-1">JUSTOFFS OFFERS</h2>
          <p className="text-xs text-slate-500 mb-3">Post a deal on JustOffs using the same business name and city — it appears on your business page automatically after approval.</p>
          <Link to="/post-ad" className="inline-flex items-center gap-2 bg-slate-800 text-white px-4 py-2.5 rounded-lg text-sm font-semibold" data-testid="manage-post-deal-btn"><Tag className="w-4 h-4" /> POST A DEAL</Link>
        </div>

        <p className="text-[11px] text-slate-400 text-center pb-6">Keep this management link private. Your QR never changes when you update your page.</p>
      </div>
    </div>
  );
};
